import { Directive, ElementRef, afterEveryRender, inject, input } from '@angular/core';

/**
 * ENF-2372 (D-4) — marks the native input of a wrapped form field (`enfaf-input`, `enfaf-datetime-picker-light`,
 * `simple-autocomplete-light`) as invalid while its error is on screen, so assistive technology announces it:
 *
 * - `aria-invalid="true"` on the input. Material leaves it out on purpose for an empty required field (it relies on
 *   `aria-required`), which is exactly the «Edición»/vigencia error case, and the wrapped components expose no input
 *   to force it.
 * - `aria-describedby` also points to an error rendered OUTSIDE the field (`describedBy`, e.g. the predecessor's
 *   message), merged with the ids Material already manages (hint, `mat-error`).
 * - `role="alert"` on the field's own `mat-error`, so the message is announced when it appears.
 *
 * The wrapped components are shared, so this only touches the rendered DOM. It re-applies after every render
 * because Material rewrites `aria-describedby` / `aria-invalid` when its own state changes.
 */
@Directive({
  selector: '[appFieldErrorA11y]'
})
export class FieldErrorA11yDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The field's error is visible. */
  readonly appFieldErrorA11y = input(false);
  /** Id of an error element rendered outside the field, referenced only while the error is visible. */
  readonly describedBy = input<string | null>(null);

  constructor() {
    afterEveryRender({ write: () => this.sync() });
  }

  private sync(): void {
    const field = this.host.nativeElement.querySelector<HTMLInputElement>('input');
    if (!field) return;
    const invalid = this.appFieldErrorA11y();
    field.setAttribute('aria-invalid', String(invalid));

    const externalId = this.describedBy();
    if (externalId) {
      const ids = (field.getAttribute('aria-describedby') ?? '').split(/\s+/).filter((id) => id && id !== externalId);
      if (invalid) ids.push(externalId);
      if (ids.length) {
        field.setAttribute('aria-describedby', ids.join(' '));
      } else {
        field.removeAttribute('aria-describedby');
      }
    }

    this.host.nativeElement.querySelectorAll('mat-error').forEach((error) => error.setAttribute('role', 'alert'));
  }
}
